import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { StatusSourceConfig } from './homeConfig';

export type StatusLevel = 'operational' | 'degraded' | 'maintenance' | 'outage' | 'unknown';

export interface ServiceStatus {
  level: StatusLevel;
  message: string;
}

const REQUEST_TIMEOUT_MS = 10000;
const CACHE_TTL_MS = 60000;

// Several chips can share one payload (GitHub and Copilot both read the
// githubstatus.com summary), so in-flight/fresh responses are reused.
const responseCache = new Map<string, { at: number; promise: Promise<string> }>();

async function fetchText(url: string, fetchApi: FetchApi): Promise<string> {
  const cached = responseCache.get(url);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetchApi.fetch(url, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.text();
    } finally {
      clearTimeout(timer);
    }
  })();
  responseCache.set(url, { at: Date.now(), promise });
  promise.catch(() => responseCache.delete(url));
  return promise;
}

interface StatuspageSummary {
  status?: { indicator?: string; description?: string };
  components?: Array<{ name: string; status: string; group?: boolean }>;
  incidents?: Array<{ name: string; components?: Array<{ name: string }> }>;
  scheduled_maintenances?: Array<{ name: string; status?: string; components?: Array<{ name: string }> }>;
}

const STATUSPAGE_INDICATORS: Record<string, StatusLevel> = {
  none: 'operational',
  minor: 'degraded',
  major: 'outage',
  critical: 'outage',
  maintenance: 'maintenance',
};

const STATUSPAGE_COMPONENT_STATUSES: Record<string, StatusLevel> = {
  operational: 'operational',
  degraded_performance: 'degraded',
  partial_outage: 'degraded',
  major_outage: 'outage',
  under_maintenance: 'maintenance',
};

function humanize(value: string): string {
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function parseStatuspage(body: string, component?: string): ServiceStatus {
  const summary = JSON.parse(body) as StatuspageSummary;

  if (component) {
    const wanted = component.toLocaleLowerCase('en-US');
    const match = summary.components?.find(c => c.name.toLocaleLowerCase('en-US') === wanted);
    if (!match) {
      return { level: 'unknown', message: `Component "${component}" not found` };
    }
    const incident = summary.incidents?.find(i =>
      i.components?.some(c => c.name.toLocaleLowerCase('en-US') === wanted),
    );
    return {
      level: STATUSPAGE_COMPONENT_STATUSES[match.status] ?? 'unknown',
      message: incident?.name ?? humanize(match.status),
    };
  }

  const indicator = summary.status?.indicator ?? '';
  const incident = summary.incidents?.[0]?.name ?? summary.scheduled_maintenances?.find(m => m.status === 'in_progress')?.name;
  return {
    level: STATUSPAGE_INDICATORS[indicator] ?? 'unknown',
    message: incident ?? summary.status?.description ?? 'Status unavailable',
  };
}

interface AzureDevOpsHealth {
  status?: { health?: string; message?: string };
  services?: Array<{ id: string; geographies?: Array<{ id: string; health: string }> }>;
}

const AZURE_DEVOPS_HEALTH: Record<string, StatusLevel> = {
  healthy: 'operational',
  advisory: 'maintenance',
  degraded: 'degraded',
  unhealthy: 'outage',
};

const SEVERITY: StatusLevel[] = ['unknown', 'operational', 'maintenance', 'degraded', 'outage'];
const worst = (levels: StatusLevel[]): StatusLevel =>
  levels.reduce<StatusLevel>((acc, level) => (SEVERITY.indexOf(level) > SEVERITY.indexOf(acc) ? level : acc), 'operational');

export function parseAzureDevOps(body: string, geography?: string): ServiceStatus {
  const health = JSON.parse(body) as AzureDevOpsHealth;

  if (geography) {
    const affected: string[] = [];
    const levels = (health.services ?? []).flatMap(service =>
      (service.geographies ?? [])
        .filter(geo => geo.id.toLocaleLowerCase('en-US') === geography.toLocaleLowerCase('en-US'))
        .map(geo => {
          const level = AZURE_DEVOPS_HEALTH[geo.health] ?? 'unknown';
          if (level !== 'operational') affected.push(service.id);
          return level;
        }),
    );
    if (levels.length === 0) {
      return { level: 'unknown', message: `Geography "${geography}" not found` };
    }
    const level = worst(levels);
    return {
      level,
      message: level === 'operational' ? 'All services healthy' : `Affected: ${affected.join(', ')}`,
    };
  }

  const overall = health.status?.health ?? '';
  return {
    level: AZURE_DEVOPS_HEALTH[overall] ?? 'unknown',
    message: health.status?.message ?? humanize(overall || 'unknown'),
  };
}

/** RSS feeds (e.g. Azure status) only list items while something is happening. */
export function parseRss(body: string): ServiceStatus {
  const doc = new DOMParser().parseFromString(body, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length > 0 || !doc.querySelector('channel')) {
    return { level: 'unknown', message: 'Status feed could not be read' };
  }
  const items = Array.from(doc.querySelectorAll('item'));
  if (items.length === 0) {
    return { level: 'operational', message: 'No active events' };
  }
  const title = items[0].querySelector('title')?.textContent?.trim();
  return {
    level: 'degraded',
    message: `${items.length} active event${items.length === 1 ? '' : 's'}${title ? `: ${title}` : ''}`,
  };
}

export async function fetchServiceStatus(
  source: StatusSourceConfig,
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<ServiceStatus> {
  try {
    const proxyBase = await discoveryApi.getBaseUrl('proxy');
    const path = source.proxyPath.startsWith('/') ? source.proxyPath : `/${source.proxyPath}`;
    const body = await fetchText(`${proxyBase}${path}`, fetchApi);
    switch (source.type) {
      case 'statuspage':
        return parseStatuspage(body, source.component);
      case 'azure-devops':
        return parseAzureDevOps(body, source.component);
      case 'rss':
        return parseRss(body);
      default:
        return { level: 'unknown', message: 'Unsupported status source' };
    }
  } catch {
    return { level: 'unknown', message: 'Status unavailable right now' };
  }
}
